<template>
  <div
    :class="
      $b({
        visible,
        animate,
        size,
        background,
        alignMobile,
        fluid,
        [`theme-${themeOverride}`]: themeOverride !== 'disabled',
      })
    "
    :style="{ height: heightWithPixels }"
  >
    <div
      :class="$b('container', { fullscreen })"
      :style="{ maxWidth: maxWidth }"
    >
      <div :class="$b('inner', { fullscreen })">
        <slot />
      </div>
    </div>

    <div
      :class="$b('overlay', { fullscreen })"
      @click="$emit('close')"
    />
  </div>
</template>

<script>
import { debounce } from '~/assets/js/helpers'

export default {
  name: 'BaseModal',
  props: {
    visible: { type: Boolean, default: false },
    animate: { type: Boolean, default: false },
    addPadding: { type: Boolean, default: false },
    fluid: { type: Boolean, default: false },
    maxWidth: { type: String, default: null },
    size: { type: String, default: 'small' },
    fullscreen: { type: Boolean, default: false },
    background: {
      type: String,
      default: 'white',
      validator: (value) => {
        return ['white', 'transparent'].includes(value)
      },
    },
    alignMobile: {
      type: String,
      default: 'center',
      validator: (value) => {
        return ['top', 'center', 'bottom'].includes(value)
      },
    },
    themeOverride: {
      type: String,
      default: 'disabled',
      validator: (value) => {
        return ['disabled', 'dark', 'gold'].includes(value)
      },
    }
  },

  emits: ['close'],

  setup(props) {
    const pageStore = usePageStore()

    if (props.visible) pageStore.toggleBlockBodyScroll(true)

    return { pageStore }
  },

  data() {
    return {
      modalOverlayHeight: null,
    }
  },
  computed: {
    heightWithPixels() {
      return this.modalOverlayHeight ? `${this.modalOverlayHeight}px` : null
    },
  },

  watch: {
    visible() {
      if (this.visible) this.pageStore.toggleBlockBodyScroll(true)
      else this.pageStore.toggleBlockBodyScroll(false)

      if (this.alignMobile === 'bottom') this.setModalOverlayHeight()
    },
  },
  mounted() {
    if (this.alignMobile === 'bottom') {
      this.setModalOverlayHeight = debounce(
        this.setModalOverlayHeight,
        500
      )
      window.addEventListener('resize', this.setModalOverlayHeight)
      this.setModalOverlayHeight()
    }
  },
  beforeUnmount() {
    this.pageStore.toggleBlockBodyScroll(false)

    if (this.alignMobile === 'bottom') {
      window.removeEventListener('resize', this.setModalOverlayHeight)
    }
  },
  methods: {
    setModalOverlayHeight() {
      this.modalOverlayHeight = window.innerHeight
    },
  },
}
</script>

<style lang="postcss">
.BaseModal {
  --transition-speed: 750ms;

  @apply fixed top-0 left-0
    w-full h-full
    flex justify-center items-center
    opacity-0
    z-50
    pointer-events-none;

  visibility: hidden;
  transition: 0s var(--transition-speed);

  @screen mdMax {
    height: 100vh;
  }

  &--visible {
    @apply visible opacity-100
      pointer-events-auto;
    transition: 0s 0s;
  }

  &--align-mobile-bottom {
    @apply smMax:h-full;
    transition: height var(--transition-speed) ease-in-out;

    .BaseModal__container {
      @apply smMax:mt-auto smMax:mb-0;
    }
  }

  &--align-mobile-top {
    @apply smMax:items-start;
  }

  &__overlay {
    @apply absolute top-0 left-0
      w-full h-full
      bg-off-black opacity-80;
    backdrop-filter: blur(2px);
    z-index: 13;
  }

  &__container {
    @apply bg-white;

    z-index: 14;
    border-radius: 10px;
  }

  &--size-large &__container {
    @apply h-full md:h-auto w-full
      bg-white
      flex flex-col justify-center overflow-hidden;
    border-radius: 0;

    @screen md {
      border-radius: 10px;
      min-width: 640px;
      max-width: 870px;
      width: 80vw;
      min-height: 500px;
      max-height: 85vh;
    }
  }

  &--size-large &__inner {
    @apply h-full w-full
      bg-white
      overflow-y-auto overflow-x-hidden scrollbar-styled;
  }

  &--fluid {
    @apply h-full
      lg:p-xs;

    @screen short {
      @apply h-full max-h-full;
    }

    .BaseModal__container {
      @apply h-full;
    }

    .BaseModal__inner {
      @apply h-full lg:flex lg:items-center;
    }
  }
}

.BaseModal--animate {
  .BaseModal__overlay {
    @apply transition opacity-0;
    transition-duration: var(--transition-speed);

    &--fullscreen {
      @apply opacity-100 bg-white fixed inset-0;
      transition: transform var(--transition-speed);
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      transform: translate3d(0, -100vh, 0);
    }
  }

  .BaseModal__container {
    transition: transform var(--transition-speed);
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transform: translate3d(0, -100vh, 0);
  }

  &.BaseModal--visible {
    .BaseModal__overlay {
      @apply opacity-80;

      &--fullscreen {
        @apply opacity-100;
        transform: translate3d(0, 0%, 0);
      }
    }

    .BaseModal__container {
      transform: translate3d(0, 0%, 0);
    }
  }
}

.BaseModal--background-transparent {
  & .BaseModal__inner,
  & .BaseModal__container {
    @apply bg-transparent w-full;
  }
}

.BaseModal .BaseModal__inner--fullscreen {
  @apply h-full;
}

.BaseModal .BaseModal__container.BaseModal__container--fullscreen {
  @apply h-full w-full rounded-none
    max-h-full max-w-full;
}
</style>
